import React from "react";
import AgentWorkflow from "../components/AgentWorkflow";
import { renderVoiceBlueprint } from "../components/blueprintRenderers";

const ACCENT = "#5e50f7";

/* ----------- Brand Építő - 5 typed questions ----------- */

const EMOTION_OPTIONS = [
  "Végre valaki ért engem",
  "Tükröt tart",
  "Megnyugtat",
  "Felráz",
  "Tisztánlátást ad",
  "Bátorságot ad",
  "Biztonságot ad",
  "Emberi és őszinte",
  "Prémium és komoly",
  "Inspirál",
];

const VIBE_SLIDERS = [
  { left: "Laza", right: "Komoly" },
  { left: "Direkt", right: "Költői" },
  { left: "Baráti", right: "Tekintélyes" },
  { left: "Egyszerű", right: "Mély" },
  { left: "Inspiráló", right: "Konkrét" },
];

const TILTOLISTA_EXAMPLES = [
  "skálázz",
  "robbantsd be",
  "manifesztálj",
  "30 nap alatt",
  "légy autentikus",
  "csak akarnod kell",
  "exponenciális",
  "lépj ki a komfortzónádból",
  "kövesd a szíved",
  "bízz a folyamatban",
  "merj nagyot álmodni",
  "áttörés",
  "csodamódszer",
  "magasabb rezgés",
  "pénzügyi szabadság",
];

const steps = [
  {
    key: "q01_celkozonseg",
    type: "textarea",
    eyebrowLabel: "01 · CÉLKÖZÖNSÉG",
    question: "Kinek segítesz, és milyen problémát oldasz meg a számukra?",
    rows: 4,
    placeholder: "pl. tapasztalt szolgáltatóknak, vagy SaaS founderöknek",
  },
  {
    key: "q02_eredmeny",
    type: "textarea",
    eyebrowLabel: "02 · EREDMÉNY",
    question: "Milyen eredményt szeretnél velük elérni?",
    rows: 4,
    placeholder: "pl. heti 5-10 ügyfélhívás, vagy heti 25 órás munkahét",
  },
  {
    key: "q03_erzes",
    type: "pills",
    eyebrowLabel: "03 · ÉRZELMI HATÁS",
    question: "Milyen érzést keltsen a márkahangod?",
    options: EMOTION_OPTIONS,
    minSelect: 2,
    maxSelect: 4,
    customPlaceholder: "Más érzés…",
    customHint: "Klikkelj 2-4 érzésre, vagy írj sajátot a Más érzés mezőbe.",
  },
  {
    key: "q04_vibe",
    type: "sliders",
    eyebrowLabel: "04 · VIBE SKÁLA",
    question: "Milyen hangulatot árasszon a márkád?",
    hint: "Húzd a csúszkát oda, ahol a márkád igazán otthon van.",
    sliders: VIBE_SLIDERS,
  },
  {
    key: "q05_tiltolista",
    type: "tags",
    eyebrowLabel: "05 · TILTÓLISTA",
    question: "Mit NEM akarsz hallani a saját márkádtól?",
    hint: "Írj be tilalmas kifejezéseket, vagy klikkelj a példákra. Enter-rel mentesz.",
    placeholder: "pl. skálázz, manifesztálj, robbantsd be",
    examplePills: TILTOLISTA_EXAMPLES,
  },
];

/* ----------- 6 Niche demo values (cycled by the single DEMO pill) ----------- */

const nichePills = [
  {
    id: "business",
    label: "Business Coach",
    values: {
      q01_celkozonseg:
        "Sikeres szolgáltató vállalkozóknak segítek átállni a »minden rajtam múlik« üzemmódról csapat- és rendszer-vezérelt működésre.",
      q02_eredmeny:
        "6 hónap alatt heti 25 órás munkahétre redukált működés, miközben az árbevétel megduplázódik - saját idő, csapat, kiszámíthatóság.",
      q03_erzes: {
        selected: ["Végre valaki ért engem", "Bátorságot ad", "Emberi és őszinte"],
        custom: "",
      },
      q04_vibe: [50, 50, 30, 70, 50],
      q05_tiltolista: ["robotolás", "mindset reset", "manifesztálj", "lépj ki a komfortzónádból", "csodamódszer"],
    },
  },
  {
    id: "marketing",
    label: "Marketing Ügynökség",
    values: {
      q01_celkozonseg:
        "5-15 fős kreatív ügynökségeknek segítek átállni projekt-alapú működésről visszatérő havi retainer modellre - anélkül, hogy ügyfelet veszítenének.",
      q02_eredmeny:
        "12 hónap alatt 70% retainer-arány az árbevételben, kiszámítható havi MRR, stresszmentes negyedévzárások.",
      q03_erzes: {
        selected: ["Tisztánlátást ad", "Biztonságot ad", "Prémium és komoly"],
        custom: "",
      },
      q04_vibe: [60, 50, 60, 50, 70],
      q05_tiltolista: ["skálázz", "vírusos siker", "exponenciális", "10-szeres növekedés"],
    },
  },
  {
    id: "parkapcs",
    label: "Párkapcsolati Coach",
    values: {
      q01_celkozonseg:
        "Komoly hosszú távú párkapcsolatban élő pároknak segítek visszaépíteni az érzelmi és intim kapcsolatot - a felgyűlt kommunikációs mintákat újratanulva.",
      q02_eredmeny:
        "4 hónap alatt rendszeresen működő érzelmi kommunikáció, megújult intimitás, és tudatos napi kapcsolódás-rituálék - anélkül, hogy bárkinek meg kellene változnia.",
      q03_erzes: {
        selected: ["Végre valaki ért engem", "Emberi és őszinte", "Biztonságot ad"],
        custom: "",
      },
      q04_vibe: [30, 70, 20, 70, 40],
      q05_tiltolista: ["toxikus", "ikerláng", "narcisszista", "kötődési minta", "magasabb rezgés"],
    },
  },
  {
    id: "mindset",
    label: "Mindset Coach",
    values: {
      q01_celkozonseg:
        "Magas teljesítményt kereső vállalkozóknak segítek leszerelni a belső szabotázsokat - ami nem motiváció kérdése, hanem mintázat-felismerés és átírás.",
      q02_eredmeny:
        "90 nap alatt felismerhető és kezelhető belső blokkok, döntésképesség stresszhelyzetben, fenntartható teljesítmény burnout nélkül.",
      q03_erzes: {
        selected: ["Tükröt tart", "Bátorságot ad", "Tisztánlátást ad"],
        custom: "",
      },
      q04_vibe: [50, 60, 40, 70, 50],
      q05_tiltolista: ["manifesztálj", "bőség-tudat", "magas frekvencia", "összhang", "magasabb rezgés"],
    },
  },
  {
    id: "health",
    label: "Health Coach",
    values: {
      q01_celkozonseg:
        "Magas teljesítményű vezetőknek és vállalkozóknak segítek visszanyerni az energiát és fókuszt - anélkül, hogy radikális diétára vagy 6 órás edzésekre lenne szükségük.",
      q02_eredmeny:
        "12 hét alatt visszatérő stabil energia, jobb alvás, és napi 4-5 óra mély munka - fenntartható módszerekkel.",
      q03_erzes: {
        selected: ["Végre valaki ért engem", "Biztonságot ad", "Emberi és őszinte"],
        custom: "",
      },
      q04_vibe: [40, 40, 40, 50, 50],
      q05_tiltolista: ["test-trükkök", "optimalizálj", "csúcsteljesítmény", "manifesztálj", "csak akarnod kell"],
    },
  },
  {
    id: "karrier",
    label: "Karrier Coach",
    values: {
      q01_celkozonseg:
        "35-50 közötti közép- és felsővezetőknek segítek karrierváltást vagy belső pozícióváltást végrehajtani - anélkül, hogy egy lépcsőfokot is hátrafelé kellene lépniük fizetésben.",
      q02_eredmeny:
        "6 hónap alatt új pozíció, 20-40%-os fizetésemeléssel, és olyan munkakör, ami illik a következő 10 év életszakaszához.",
      q03_erzes: {
        selected: ["Tisztánlátást ad", "Bátorságot ad", "Prémium és komoly"],
        custom: "",
      },
      q04_vibe: [60, 40, 60, 50, 70],
      q05_tiltolista: ["álommunka", "kövesd a szenvedélyed", "álomkarrier", "találd meg az értelmet", "másodállás"],
    },
  },
];

/* ----------- Serialize guided values → backend payload -----------
 * Produces one Hungarian pre-formatted block and sends it as `user_context`.
 * The optional `content` arg carries the 3-part content input section
 * ({paste, urls}); when anything is filled, an "Írásminta:" block is
 * appended (file content itself is picked up via RAG on the backend).
 */
function serializeValues(v, content) {
  const celkozonseg = (v.q01_celkozonseg || "").trim();
  const eredmeny = (v.q02_eredmeny || "").trim();
  const erzes = v.q03_erzes || { selected: [], custom: "" };
  const erzesParts = Array.isArray(erzes.selected) ? [...erzes.selected] : [];
  if ((erzes.custom || "").trim()) erzesParts.push(erzes.custom.trim());
  const sliders = Array.isArray(v.q04_vibe) && v.q04_vibe.length === VIBE_SLIDERS.length
    ? v.q04_vibe
    : VIBE_SLIDERS.map(() => 50);
  const tags = Array.isArray(v.q05_tiltolista) ? v.q05_tiltolista : [];

  const lines = [
    `Célközönség: ${celkozonseg}`,
    `Eredmény: ${eredmeny}`,
    `Érzelmi hatás: ${erzesParts.join(", ")}`,
    "Vibe skála:",
    ...VIBE_SLIDERS.map((pair, i) => `- ${pair.left}↔${pair.right}: ${sliders[i]}/100`),
    `Tiltólista: ${tags.join(", ")}`,
  ];

  // Append optional content-input section ("Írásminta:" block)
  const paste = (content?.paste || "").trim();
  const urls = Array.isArray(content?.urls) ? content.urls.filter(Boolean) : [];
  if (paste || urls.length > 0) {
    lines.push("", "Írásminta:");
    if (paste) lines.push(paste);
    if (urls.length > 0) {
      lines.push(...urls.map((u) => `- Forrás URL: ${u}`));
    }
  }

  return { user_context: lines.join("\n") };
}

/**
 * Reverse map: backend `inputs` → contentState for the Beállítás nézet.
 * Márkahang has two modes with distinct payload shapes:
 *   - Voice Express (fastVariant="upload"): AgentInputScreen dupes
 *     `writingSample` into all 5 fastTrackKeys (pain/result/method/
 *     credibility/vibe), and `user_context` stays null.
 *   - Voice Építő: serializeValues packs the 5 guided questions into
 *     `user_context` as a pre-formatted Hungarian block.
 *
 * We recover Express state when all 5 fastTrack keys share the same
 * non-empty text and `user_context` is empty. That text is restored to
 * `writingSample` and mode is set to "fast".
 */
function deserializeValues(inputs) {
  if (!inputs || typeof inputs !== "object") return null;
  const uc = (inputs.user_context || "").trim();
  const pain = (inputs.pain || "").trim();
  const result = (inputs.result || "").trim();
  const method = (inputs.method || "").trim();
  const credibility = (inputs.credibility || "").trim();
  const vibe = (inputs.vibe || "").trim();
  const allSame = pain && pain === result && pain === method && pain === credibility && pain === vibe;
  if (!uc && allSame) {
    return { mode: "fast", writingSample: pain, paste: "", urls: [] };
  }
  return null;
}

export default function BrandVoice() {
  return (
    <AgentWorkflow
      accent={ACCENT}
      agentKey="voice"
      dashboardKey="voice"
      blueprintName="Márkahang (Brand Voice)"
      saveEndpoint="/foundation/brand-voice/save"
      eyebrow="ALAPOZÁS · 01 · HANG"
      title="Márkahang Varázsló"
      agentName="Márkahang Varázsló™"
      deliverableLabel="Márkahang"
      readyTitle="Elkészült a Márkahangod."
      subtitle="Öt kérdés. Egy blueprint. A márkahangod, amit visszaismersz amint meglátod."
      greeting="Szia! Megkeresem a szövegeidben azt a hangot, amitől felismerhetően te vagy. Ebből az alapból kiindulva minden anyagod automatikusan a te stílusodban születik újjá."
      fastLabel="Márkahang Expressz"
      fastDesc="Elemzés és stílusprofil meglévő anyagból"
      guidedLabel="Márkahang Építő"
      guidedDesc="Karakteres hangolás irányított kérdésekkel"
      modePrompt=""
      steps={steps}
      nichePills={nichePills}
      fastTrackKeys={["pain", "result", "method", "credibility", "vibe"]}
      serializeValues={serializeValues}
      deserializeValues={deserializeValues}
      cycleDemoPill={true}
      contentInputMode="split"
      generateEndpoint="/foundation/brand-voice/generate"
      loadEndpoint="/foundation/brand-voice"
      refineEndpoint="/foundation/voice/refine"
      renderBlueprint={renderVoiceBlueprint}
    />
  );
}
